import { AppModules } from '@/types';
import { Role } from '@/types/api';
import { HeaderMenuOptionType, SidebarOptionType } from '@/types/routes';

export const getEndpointWithParams = (
  endpoint: string,
  object: { [key: string]: string | number | undefined }
): string => {
  let endpointWithParams = endpoint;

  const objectKeys = Object.keys(object);

  objectKeys.forEach((key: string) => {
    endpointWithParams = endpointWithParams.replace(`:${key}`, String(object[key]));
  });

  return endpointWithParams;
};

/**
 * Normalize a path for permission checking.
 * Maps new /talentx/ and /peoplex/ prefixes back to /hiring/ and /people/
 * so existing backend permission artifacts still work.
 */
const normalizePath = (path: string): string =>
  path.replace(/^\/talentx\//, '/hiring/').replace(/^\/peoplex\//, '/people/');

const doesSidebarExistInPermissions = ({
  sidebar,
  userPermissions,
}: {
  sidebar: SidebarOptionType;
  userPermissions: string[];
}): boolean => {
  const artifact = `web:${sidebar.path}`;
  if (userPermissions.includes(artifact)) return true;

  // Also check the legacy (normalized) path for backwards compatibility
  const legacyArtifact = `web:${normalizePath(sidebar.path)}`;
  return userPermissions.includes(legacyArtifact);
};

export const getAppMenu = (menu: HeaderMenuOptionType[], roles: Role[]): HeaderMenuOptionType[] => {
  if (!roles) {
    return [];
  }

  let userPermissions: string[] = [];

  roles.forEach((role) => {
    const rolePermissionValues = role.permissions.map((permission) => permission.value);
    userPermissions = [...userPermissions, ...rolePermissionValues];
  });

  const allowedMenus = menu.map((menuOption: HeaderMenuOptionType) => {
    // Settings always shows all sidebar items
    if (menuOption.module === AppModules.SETTINGS) return menuOption;

    const allowedSidebarMenus = menuOption.sidebarMenus.filter((sidebar) =>
      doesSidebarExistInPermissions({ sidebar, userPermissions })
    );

    return {
      ...menuOption,
      sidebarMenus: allowedSidebarMenus,
    };
  });

  const menusWithoutEmptySidebar = allowedMenus.filter(
    (menuOption) =>
      !!menuOption.sidebarMenus.length ||
      menuOption.module === AppModules.HOME ||
      menuOption.module === AppModules.SETTINGS
  );

  return menusWithoutEmptySidebar;
};
